import React, { useState } from "react";
//styling
import loginStyles from "./Login.module.scss";
// Mui
import { CircularProgress, Grid2 } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
//Local import
import ReCAPTCHA from "react-google-recaptcha";
import { signIn, fetchAuthSession } from "aws-amplify/auth";
import { Actions } from "../../../../redux/constants/actionTypes";
import { getStore } from "../../../../Utils/storeUtils";
import AuthContext from "../../../../store/auth-context";
import { useNavigate, useLocation } from "react-router-dom";
import CustomButton from "../../../../GlobalComponents/CustomButton/CustomButton";
import axios from "axios";
import {
  getAuthorizationHeader,
  verifyEmail,
  verifyPassword,
} from "../../../../Utils/utils";
import "react-toastify/dist/ReactToastify.css";
import classNames from "classnames";

//Recaptcha
const recaptchaStatus = process.env.REACT_APP_RECAPTCHA_LOGIN;
const recaptchSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function NewLogin() {
  // Props and Hooks
  const [captchaToken, setCaptchaToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const authCtx = React.useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const prefillEmail = new URLSearchParams(location.search).get("email") || "";

  const [values, setValues] = React.useState({
    email: prefillEmail,
    emailState: prefillEmail ? "success" : "error",
    emailErrorMessage: "Email is Required",
    password: "",
    passwordState: "error",
    passwordErrorMessage: "Password is Required",
  });

  const [inputErrorVar, setInputErrorVar] = React.useState({
    email: false,
    password: false,
    captcha: false,
  });

  // // Determine if login button should be enabled
  // const canLogin = values.email && values.password && captchaToken;

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (
      values.emailState === "error" ||
      values.passwordState === "error" ||
      !captchaToken ||
      recaptchaStatus !== "true"
    ) {
      setInputErrorVar({
        email: values.emailState === "error",
        password: values.passwordState === "error",
        captcha: !captchaToken,
        recaptchaStatus: recaptchaStatus !== "true",
      });
      return;
    }

    setInputErrorVar({
      email: values.emailState === "error",
      password: values.passwordState === "error",
      captcha: !captchaToken,
      recaptchaStatus: recaptchaStatus !== "true",
    });
    setError(null);
    setLoading(true);
    try {
      let signInResponse = await signIn({
        username: values.email,
        password: values.password,
        options: {
          clientMetadata: {
            captcha: captchaToken,
          },
        },
      });

      if (signInResponse?.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        navigate(`/verify`, {
          state: {
            email: values.email,
            prevPage: "REGISTER",
          },
        });
      }

      let sessionDetails = await fetchAuthSession();

      // let userSession = signInResponse.signInUserSession;
      getStore().dispatch({
        type: Actions.LOGIN_USER,
        userInfo: sessionDetails.tokens?.idToken?.payload,
      });

      const expirationTime = new Date(
        sessionDetails.tokens?.accessToken.payload.exp * 1000
      );
      await authCtx
        .login(
          sessionDetails.tokens?.accessToken,
          expirationTime.toISOString(),
          sessionDetails,
          sessionDetails.tokens.accessToken.toString()
        )
        .then();

      const res = await axios.post(
        `${process.env.REACT_APP_BACK_END_API_BASE_URL}/users`,
        {
          cognitoId: sessionDetails?.userSub,
          emailId: sessionDetails.tokens?.idToken?.payload?.email,
          userName: sessionDetails.tokens?.idToken?.payload["cognito:username"],
          phoneNumber: sessionDetails.tokens?.idToken?.payload?.phone_number,
          country: sessionDetails.tokens?.idToken?.payload["custom:country"],
          zipCode: sessionDetails.tokens?.idToken?.payload["custom:ZipCode"],
          state: sessionDetails.tokens?.idToken?.payload["custom:state"],
          roleID: 1,
        },
        getAuthorizationHeader()
      );

      navigate("/account/start");
    } catch (error) {
      setCaptchaToken(true);
      if (error.message === "Incorrect username or password.") {
        setError(error.message);
      } else {
        setError("Login failed. Please try again later.");
      }
      authCtx.logout();
      if (window.grecaptcha && typeof window.grecaptcha.reset === "function") {
        window.grecaptcha.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  const onChange = (value) => {
    setCaptchaToken(value);
    setError(null);
    setInputErrorVar((prev) => ({ ...prev, captcha: false }));
  };

  const handleChange = (prop, type, value, length) => {
    let status = "";
    let message = "";
    switch (type) {
      case "email":
        value = value.replace(" ", "");
        if (value === "") {
          status = "error";
          message = "Email is Required";
        } else if (verifyEmail(value)) {
          status = "success";
        } else {
          status = "error";
          message = "Please enter a valid email";
        }
        break;
      case "length":
        value = value.replace(" ", "");
        if (value === "") {
          status = "error";
          message = "Password is Required";
        } else if (verifyPassword(value, length)) {
          status = "success";
          message = "";
        } else {
          status = "error";
          message =
            "Password should be 8 char long with upper & lower case, number and special char";
        }
        break;
      default:
        break;
    }
    setError(null);
    setInputErrorVar((prevInputErrorVar) => ({
      ...prevInputErrorVar,
      [prop]: false,
    }));
    setValues({
      ...values,
      [prop + "State"]: status,
      [prop + "ErrorMessage"]: message,
      [prop]: value,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
    if (
      e.key === "Enter" &&
      !loading &&
      values.emailState === "success" &&
      values.passwordState === "success"
    ) {
      handleLogin();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  let domain = window.location.hostname;
  domain =
    domain === "localhost" ? process.env.REACT_APP_METADATA_DOMAIN : domain;

  return (
    <>
      <form onSubmit={handleLogin} noValidate>
        <Grid2 className={loginStyles.LoginContainer}>
          <Grid2 className={loginStyles.Header}>Sign In</Grid2>
          <Grid2>
            <Grid2 className={loginStyles.subHeader}>
              Glad to See You Again!
            </Grid2>
            <Grid2 className={loginStyles.subHeader1}>
              Please sign in to access your account
            </Grid2>
          </Grid2>
          <Grid2 className={loginStyles.InputFlexbox}>
            <Grid2>
              <Grid2>
                <input
                  className={classNames(loginStyles.inputForm, {
                    [loginStyles.errorInput]: inputErrorVar.email,
                  })}
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={values.email}
                  onChange={(e) =>
                    handleChange(
                      "email",
                      "email",
                      e.target.value.toLowerCase().replace(/\s/g, "")
                    )
                  }
                  onKeyPress={handleKeyPress}
                  autoComplete="email"
                />
              </Grid2>

              {inputErrorVar.email && (
                <span className={loginStyles.error}>
                  {values.emailErrorMessage}
                </span>
              )}
            </Grid2>
            <Grid2>
              <Grid2>
                <Grid2 style={{ display: "flex", position: "relative" }}>
                  <input
                    className={classNames(loginStyles.inputForm, {
                      [loginStyles.errorInput]: inputErrorVar.password,
                    })}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    value={values.password}
                    onChange={(e) =>
                      handleChange("password", "length", e.target.value, 8)
                    }
                    onKeyPress={handleKeyPress}
                    autoComplete="current-password"
                  />
                  <Grid2
                    className={loginStyles.passwordIcon}
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </Grid2>
                </Grid2>

                {inputErrorVar.password && (
                  <span className={loginStyles.error}>
                    {values.passwordErrorMessage}
                  </span>
                )}
              </Grid2>
              <Grid2 className={loginStyles.error}>{error}</Grid2>
              <Grid2 className={loginStyles.forgotPassword}>
                <Grid2
                  className={loginStyles.forgotPasswordText}
                  onClick={() => navigate("/forgot")}
                >
                  Forgot Your Password?
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>

          <Grid2 className={loginStyles.recaptchFlexbox}>
            {recaptchaStatus === "true" && (
              <>
                <Grid2 className={loginStyles.recaptch}>
                  <ReCAPTCHA sitekey={recaptchSiteKey} onChange={onChange} />
                </Grid2>
                {inputErrorVar.captcha && (
                  <span className={loginStyles.error}>
                    Please verify you are not a robot
                  </span>
                )}
              </>
            )}

            <CustomButton
              className={loginStyles.login}
              data-testid="login-button"
              fullWidth={true}
              color={"primary"}
              type="submit"
            >
              {loading ? (
                <CircularProgress size={25} color={"inherit"} />
              ) : (
                "Sign In"
              )}
            </CustomButton>
          </Grid2>
          <Grid2 className={loginStyles.footerText}>
            <Grid2>Don’t have an account?</Grid2>
            <Grid2
              className={loginStyles.createAccountText}
              onClick={() => {
                if (domain === process.env.REACT_APP_FRONTEND_DOMAIN) {
                  window.open(
                    "https://roconpaas.com/managed-wordpress-hosting-pricing/",
                    "_blank"
                  );
                } else {
                  navigate("/register");
                }
              }}
            >
              {domain === process.env.REACT_APP_FRONTEND_DOMAIN
                ? "Join Us Today!"
                : "Sign Up"}
            </Grid2>
          </Grid2>
        </Grid2>
      </form>
    </>
  );
}
